import { AnimatePresence, motion } from "framer-motion";
import { useLiveQuery } from "dexie-react-hooks";
import {
  Calendar as CalendarIcon,
  ChevronLeft,
  ChevronRight,
  CreditCard,
  Edit3,
  Plus,
  ShieldCheck,
  Sparkles,
  Trash2,
  User,
  X,
  Zap,
} from "lucide-react";
import React, { useMemo, useState } from "react";
import { cn } from "@repo/core";
import { TransactionModal } from "../components/TransactionModal";
import { AppCard, KpiCard, KpiIcons, SectionTitle } from "../components/ui/AppCard";
import { useCategories, useTransactionsByDate } from "../hooks/useBudgetData";
import { useCurrency } from "../hooks/useCurrency";
import {
  calendarStartsOnMonday,
  monthLongLabel,
  monthShortLabel,
  weekdaysForLocale,
} from "../i18n/dates";
import { categoryLabelById, personLabel } from "../i18n/labels";
import { useT } from "../i18n/useT";
import { db } from "../lib/db";
import type { Transaction } from "../lib/schema";

// ── Day Details & Quick-Action Modal ─────────────────────────
function DayModal({
  date,
  onClose,
}: {
  date: string;
  onClose: () => void;
}) {
  const { format } = useCurrency();
  const { t, locale } = useT();
  const txs = useTransactionsByDate(date);
  const categories = useCategories();
  const accounts = useLiveQuery(() => db.accounts.toArray(), []) ?? [];

  const [txModalOpen, setTxModalOpen] = useState(false);
  const [editingTx, setEditingTx] = useState<Transaction | null>(null);

  const catName = (id: string) => categoryLabelById(id, categories, t);
  const catIcon = (id: string) => categories.find((c) => c.id === id)?.icon ?? "🏷️";
  const accName = (id: string) => accounts.find((a) => a.id === id)?.name ?? t("common.mainAccount");

  const handleDelete = async (id: string) => {
    if (confirm(t("tx.deleteConfirm"))) {
      await db.transactions.delete(id);
    }
  };

  const handleOpenAdd = () => {
    setEditingTx(null);
    setTxModalOpen(true);
  };

  const handleOpenEdit = (tx: Transaction) => {
    setEditingTx(tx);
    setTxModalOpen(true);
  };

  const dayExpenses = txs.filter((tx) => tx.type === "expense").reduce((s, tx) => s + tx.amount, 0);
  const dayIncome = txs.filter((tx) => tx.type === "income").reduce((s, tx) => s + tx.amount, 0);

  // Human readable date string: e.g. "Friday, Aug 7, 2026"
  const formattedDate = useMemo(() => {
    try {
      const parts = date.split("-");
      if (parts.length === 3) {
        const d = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
        return d.toLocaleDateString(locale, {
          weekday: "long",
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      }
    } catch {}
    return date;
  }, [date, locale]);

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          className="fixed inset-0 bg-black/55 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal Card */}
        <motion.div
          initial={{ opacity: 0, scale: 0.94, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.94, y: 14 }}
          transition={{ type: "spring", damping: 26, stiffness: 360 }}
          className="relative w-full max-w-lg bg-card border border-border/80 rounded-2xl shadow-2xl overflow-hidden z-10 flex flex-col max-h-[88vh]"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-border/60 bg-muted/20">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary/15 text-primary flex items-center justify-center shadow-xs">
                <CalendarIcon size={20} />
              </div>
              <div>
                <h2
                  className="text-base sm:text-lg font-semibold text-foreground tracking-tight leading-none"
                  style={{ fontFamily: "var(--font-display, inherit)" }}
                >
                  {formattedDate}
                </h2>
                <div className="flex items-center gap-2 mt-1">
                  {dayExpenses === 0 ? (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                      <Sparkles size={11} /> {t("cal.badge.noSpend")}
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-600 dark:text-rose-400 bg-rose-500/10 px-2 py-0.5 rounded-full border border-rose-500/20">
                      {t("cal.badge.spent")} {format(dayExpenses)}
                    </span>
                  )}
                  {dayIncome > 0 && (
                    <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-full border border-emerald-500/20">
                      {t("cal.badge.income")} +{format(dayIncome)}
                    </span>
                  )}
                </div>
              </div>
            </div>

            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors"
              aria-label={t("common.close")}
            >
              <X size={16} />
            </button>
          </div>

          {/* Content list */}
          <div className="flex-1 overflow-y-auto p-5 space-y-2.5">
            {txs.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center rounded-xl border border-dashed border-border/80 bg-muted/20">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 flex items-center justify-center mb-2.5 shadow-xs">
                  <ShieldCheck size={26} />
                </div>
                <p className="text-sm font-semibold text-foreground">{t("cal.cleanTitle")}</p>
                <p className="text-xs text-muted-foreground max-w-xs mt-1">
                  {t("cal.cleanBody")}
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground mb-2">
                  {t("cal.txOnDate", { n: txs.length })}
                </p>
                {txs.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center gap-3 p-3 rounded-xl border border-border/70 bg-card hover:bg-muted/30 transition-all shadow-xs group"
                  >
                    <div className="w-9 h-9 rounded-lg bg-muted/60 text-lg flex items-center justify-center shrink-0">
                      {catIcon(tx.categoryId)}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="font-semibold text-xs text-foreground truncate">{catName(tx.categoryId)}</p>
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-muted text-muted-foreground capitalize">
                          {t(`type.${tx.type}`)}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-[11px] text-muted-foreground mt-0.5 truncate">
                        <span className="inline-flex items-center gap-0.5">
                          <CreditCard size={10} className="opacity-70" />
                          {accName(tx.accountId)}
                        </span>
                        {tx.person && (
                          <>
                            <span>·</span>
                            <span className="inline-flex items-center gap-0.5">
                              <User size={10} className="opacity-70" />
                              {personLabel(tx.person, t)}
                            </span>
                          </>
                        )}
                        {tx.notes && (
                          <>
                            <span>·</span>
                            <span className="truncate italic">"{tx.notes}"</span>
                          </>
                        )}
                      </div>
                    </div>

                    <div className="text-right shrink-0">
                      <p
                        className={cn(
                          "font-bold font-mono text-sm tabular-nums",
                          tx.type === "income"
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-foreground"
                        )}
                      >
                        {tx.type === "income" ? "+" : "-"}{format(tx.amount)}
                      </p>
                    </div>

                    <div className="flex items-center gap-1 shrink-0 opacity-80 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => handleOpenEdit(tx)}
                        className="p-1.5 rounded-md hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                        title={t("tx.editAria")}
                      >
                        <Edit3 size={13} />
                      </button>
                      <button
                        onClick={() => handleDelete(tx.id)}
                        className="p-1.5 rounded-md hover:bg-destructive/10 text-muted-foreground hover:text-destructive transition-colors"
                        title={t("tx.deleteAria")}
                      >
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer Action */}
          <div className="p-4 border-t border-border/60 bg-muted/20 flex gap-2">
            <button
              onClick={handleOpenAdd}
              className="w-full py-2.5 px-4 text-xs font-semibold rounded-xl bg-primary text-primary-foreground shadow-sm hover:opacity-90 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
            >
              <Plus size={15} strokeWidth={2.5} />
              <span>{t("cal.addForDate")}</span>
            </button>
          </div>
        </motion.div>
      </div>

      {/* Embedded Transaction Modal for Add/Edit */}
      <TransactionModal
        open={txModalOpen}
        onClose={() => setTxModalOpen(false)}
        defaultDate={date}
        initialData={editingTx}
      />
    </>
  );
}

// ── Main Page ────────────────────────────────────────────────
export function SpendingCalendarPage({ year, person }: { year: number; person: string }) {
  const [month, setMonth] = useState(() => new Date().getMonth() + 1);
  const [selectedDate, setSelectedDate] = useState<string | null>(null);
  const { format } = useCurrency();
  const { t, locale } = useT();

  // All txs for this year + optional person
  const allTxs = useLiveQuery(
    () =>
      db.transactions
        .filter((tx) => {
          const matchYear   = tx.date.startsWith(String(year));
          const matchPerson = person === "All" || tx.person === person;
          return matchYear && matchPerson;
        })
        .toArray(),
    [year, person]
  ) ?? [];

  // Month-filtered txs
  const monthPrefix = `${year}-${String(month).padStart(2, "0")}`;
  const monthTxs    = useMemo(
    () => allTxs.filter((tx) => tx.date.startsWith(monthPrefix) && tx.type === "expense"),
    [allTxs, monthPrefix]
  );

  // Daily spend map: { "YYYY-MM-DD": number }
  const spendByDay = useMemo(() => {
    const map: Record<string, number> = {};
    monthTxs.forEach((tx) => {
      map[tx.date] = (map[tx.date] ?? 0) + tx.amount;
    });
    return map;
  }, [monthTxs]);

  // Calendar grid calculation — Mon-first for es/most locales, Sun-first for en
  const daysInMonth = new Date(year, month, 0).getDate();
  const startsMonday = calendarStartsOnMonday(locale);
  const jsDow = new Date(year, month - 1, 1).getDay(); // 0=Sun … 6=Sat
  const firstDayDow = startsMonday ? (jsDow + 6) % 7 : jsDow;
  const dayLabels = weekdaysForLocale(locale);

  const cells = useMemo(() => {
    const arr: { day: number | null; dateStr: string; amount: number }[] = [];
    for (let i = 0; i < firstDayDow; i++) {
      arr.push({ day: null, dateStr: "", amount: 0 });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${monthPrefix}-${String(d).padStart(2, "0")}`;
      arr.push({ day: d, dateStr, amount: spendByDay[dateStr] ?? 0 });
    }
    return arr;
  }, [firstDayDow, daysInMonth, monthPrefix, spendByDay]);

  // Streak / stat calculations
  const today        = new Date();
  const maxDay       = (year === today.getFullYear() && month === today.getMonth() + 1)
    ? today.getDate()
    : daysInMonth;

  let streak = 0;
  for (let d = maxDay; d >= 1; d--) {
    const ds = `${monthPrefix}-${String(d).padStart(2, "0")}`;
    if (!spendByDay[ds]) streak++;
    else break;
  }

  const noSpendDays = useMemo(() => {
    let count = 0;
    for (let d = 1; d <= maxDay; d++) {
      const ds = `${monthPrefix}-${String(d).padStart(2, "0")}`;
      if (!spendByDay[ds]) count++;
    }
    return count;
  }, [maxDay, monthPrefix, spendByDay]);

  const spendDays  = maxDay - noSpendDays;
  const totalSpend = monthTxs.reduce((s, tx) => s + tx.amount, 0);
  const maxSpend   = Math.max(...Object.values(spendByDay), 1);

  const prevMonth = () => setMonth((m) => (m === 1 ? 12 : m - 1));
  const nextMonth = () => setMonth((m) => (m === 12 ? 1 : m + 1));

  const handleDayClick = (day: number) => {
    const dateStr = `${monthPrefix}-${String(day).padStart(2, "0")}`;
    setSelectedDate(dateStr);
  };

  const avgPerDay = format(spendDays > 0 ? totalSpend / spendDays : 0, true);

  return (
    <div className="p-4 lg:p-6 space-y-5 w-full">
      {/* Header controls */}
      <div className="flex items-center justify-between">
        <div>
          <h2
            className="text-lg font-semibold text-foreground leading-none"
            style={{ fontFamily: "var(--font-display, inherit)" }}
          >
            {monthLongLabel(month, locale, year)} {year}
          </h2>
          <p className="text-xs text-muted-foreground mt-1">{t("cal.subtitle")}</p>
        </div>
        <div className="flex items-center gap-1.5">
          <button onClick={prevMonth} className="app-btn-outline p-1.5" aria-label={t("cal.prev")}>
            <ChevronLeft size={15} />
          </button>
          <span className="text-xs font-semibold px-2 text-muted-foreground">
            {monthShortLabel(month, locale, year)}
          </span>
          <button onClick={nextMonth} className="app-btn-outline p-1.5" aria-label={t("cal.next")}>
            <ChevronRight size={15} />
          </button>
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <KpiCard
          label={t("cal.streak")}
          value={streak}
          sub={t("cal.streakSub")}
          color="lemon"
          icon={<KpiIcons.flame size={16} />}
          index={0}
        />
        <KpiCard
          label={t("cal.noSpendDays")}
          value={noSpendDays}
          sub={t("cal.thisMonth")}
          color="mint"
          icon={<KpiIcons.up size={16} />}
          index={1}
        />
        <KpiCard
          label={t("cal.spendDays")}
          value={spendDays}
          sub={t("cal.active")}
          color="coral"
          icon={<KpiIcons.moneyOut size={16} />}
          index={2}
        />
        <KpiCard
          label={t("cal.totalSpent")}
          value={format(totalSpend, true)}
          sub={t("cal.avgDay", { amount: avgPerDay })}
          color="sky"
          icon={<KpiIcons.moneyIn size={16} />}
          index={3}
        />
      </div>

      {/* Calendar grid */}
      <div>
        <SectionTitle>{t("cal.title")}</SectionTitle>
        <AppCard padding="none" className="overflow-hidden">
          {/* Day headers */}
          <div className="grid grid-cols-7 border-b border-border bg-muted/50">
            {dayLabels.map((d) => (
              <div key={d} className="py-2 text-center text-[9px] font-semibold uppercase tracking-widest text-muted-foreground">
                {d}
              </div>
            ))}
          </div>
          {/* Day cells */}
          <div className="grid grid-cols-7">
            {cells.map((cell, i) => {
              if (!cell.day) {
                return <div key={i} className="aspect-square border-r border-b border-border/50 last:border-r-0 bg-muted/20" />;
              }

              const isToday = today.getDate() === cell.day && today.getMonth() + 1 === month && today.getFullYear() === year;
              const hasSpend = cell.amount > 0;
              const intensity = hasSpend ? cell.amount / maxSpend : 0;

              return (
                <div
                  key={cell.day}
                  onClick={() => handleDayClick(cell.day!)}
                  className={cn(
                    "aspect-square border-r border-b border-border/50 last:border-r-0 flex flex-col items-start justify-between p-1.5 transition-all cursor-pointer select-none hover:ring-2 hover:ring-primary/40 hover:z-10",
                    isToday && "bg-primary/8 ring-1 ring-inset ring-primary/30",
                    !hasSpend && !isToday && "bg-emerald-50/60 dark:bg-emerald-950/20",
                    hasSpend && !isToday && "bg-card"
                  )}
                  style={
                    hasSpend && !isToday
                      ? { backgroundColor: `rgba(239,68,68,${0.05 + intensity * 0.25})` }
                      : undefined
                  }
                >
                  <div className="flex items-center justify-between w-full">
                    <span className={cn("text-[10px] font-medium", isToday ? "text-primary font-bold" : "text-muted-foreground")}>
                      {cell.day}
                    </span>
                    {!hasSpend && !isToday && <Zap size={8} className="text-emerald-500" />}
                  </div>
                  {hasSpend && (
                    <span className="text-[9px] font-semibold text-muted-foreground leading-none self-end">
                      {format(cell.amount, true)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </AppCard>

        {/* Legend */}
        <div className="flex gap-5 mt-3 text-[10px] text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-emerald-100 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-800" />
            <span>{t("cal.legend.noSpend")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm bg-rose-100 border border-rose-200" />
            <span>{t("cal.legend.spend")}</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded-sm ring-1 ring-primary/30 bg-primary/10" />
            <span>{t("cal.legend.today")}</span>
          </div>
        </div>
      </div>

      {/* Interactive Day Details Modal */}
      <AnimatePresence>
        {selectedDate && (
          <DayModal
            date={selectedDate}
            onClose={() => setSelectedDate(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
}
